import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Wrench } from "lucide-react";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import MinorWorksStep1 from "@/components/certificates/MinorWorksStep1";
import MinorWorksStep2 from "@/components/certificates/MinorWorksStep2";
import MinorWorksStep3 from "@/components/certificates/MinorWorksStep3";
import RecentAddressPicker from "@/components/certificates/RecentAddressPicker";
import { authFetch } from "@/utils/authFetch";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { num: 1, label: "Works & Earthing" },
  { num: 2, label: "Circuit & Testing" },
  { num: 3, label: "Review & Generate" },
];

const INITIAL_FORM = {
  // Part 1 - Details
  client_name: "", client_email: "",
  description_of_works: "",
  installation_address: "", installation_postcode: "",
  departures: "", comments_existing: "", risk_assessment: "N/A",
  // Part 2 - Earthing
  system_earthing: "", zdb: "", adequate_main_conductors: "",
  bonding_water: "N/A", bonding_gas: "N/A", bonding_oil: "N/A",
  bonding_structural: "N/A", bonding_other: "", earthing_conductor: "",
  // Part 3 - Circuit
  db_ref: "", db_location_type: "", circuit_number: "", circuit_description: "",
  reference_method: "", conductor_live_size: "", conductor_live_count: "",
  conductor_cpc_size: "",
  ocpd_bs: "", ocpd_type: "", ocpd_rating: "", ocpd_breaking: "", time_delay: "",
  rcd_bs: "", rcd_type: "", rcd_rating: "", rcd_idn: "",
  afdd_rating: "", spd_type: "",
  // Part 4 - Test Results
  r1r2: "", max_zs: "", ring_ll: "", ring_nn: "", ring_cpc: "",
  ir_test_voltage: "500", ir_live_live: "", ir_live_earth: "",
  rcd_disconnect_time: "", rcd_test_btn: "N/A", polarity: "N/A",
  afdd_test_btn: "N/A", spd_confirmed: "N/A",
  // Part 5 - Declaration
  electrician_name: "", cert_date: null,
  contractor_trading_title: "", contractor_email: "", contractor_phone: "",
  contractor_address: "", contractor_postcode: "",
  contractor_reg_no: "", contractor_branch_no: "",
};

export default function MinorWorksWizard() {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [editCertId, setEditCertId] = useState(null);
  const [formData, setFormData] = useState({
    ...INITIAL_FORM,
    electrician_name: user?.name || "",
    contractor_trading_title: user?.company || "",
    contractor_email: user?.email || "",
  });

  useEffect(() => {
    // Check for edit data
    const editData = sessionStorage.getItem("edit_cert_data");
    if (editData) {
      try {
        const cert = JSON.parse(editData);
        sessionStorage.removeItem("edit_cert_data");
        setEditCertId(cert._edit_id);
        setFormData((prev) => ({ ...prev, ...cert, cert_date: null }));
        toast.info("Editing certificate — update and regenerate for free");
        return;
      } catch { /* ignore */ }
    }
    // Check for duplicate
    const dupData = sessionStorage.getItem("duplicate_cert_data");
    if (dupData) {
      try {
        const cert = JSON.parse(dupData);
        sessionStorage.removeItem("duplicate_cert_data");
        setFormData((prev) => ({ ...prev, ...cert, cert_date: null }));
        return;
      } catch { /* ignore */ }
    }
    authFetch(`${API}/api/user/contractor-details`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : {})
      .then((saved) => {
        if (saved && saved.contractor_trading_title) {
          setFormData((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(saved).filter(([, v]) => v)) }));
        }
      }).catch(() => {});
  }, []);

  const updateForm = (updates) => setFormData((prev) => ({ ...prev, ...updates }));
  const nextStep = () => setStep((s) => Math.min(s + 1, 3));
  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }} data-testid="minor-works-wizard">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-3 mb-4">
          <Wrench className="w-6 h-6 text-[#00A3E0]" />
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Minor Electrical Installation Works Certificate
          </h1>
        </div>

        <RecentAddressPicker onSelect={(a) => updateForm({
          installation_address: a.address, installation_postcode: a.postcode,
          client_name: a.client_name || formData.client_name,
          client_email: a.client_email || formData.client_email,
        })} />

        {/* Step Indicator */}
        <div className="flex items-center justify-between mb-8 mt-6" data-testid="mw-step-indicator">
          {STEPS.map((s, i) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div
                  data-testid={`mw-step-${s.num}-indicator`}
                  className={`w-10 h-10 flex items-center justify-center border-2 text-sm font-bold transition-all ${
                    step > s.num ? "step-completed" : step === s.num ? "step-active" : "step-inactive"
                  }`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span className={`text-xs mt-2 font-medium text-center hidden sm:block ${step >= s.num ? "text-[#0A0A0A]" : "text-[#B8B9BD]"}`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 sm:mx-4 ${step > s.num ? "bg-[#00A3E0]" : "bg-[#E4E4E7]"}`} />
              )}
            </div>
          ))}
        </div>

        <div className="animate-fade-in-up" key={step}>
          {step === 1 && <MinorWorksStep1 formData={formData} updateForm={updateForm} onNext={nextStep} />}
          {step === 2 && <MinorWorksStep2 formData={formData} updateForm={updateForm} onNext={nextStep} onPrev={prevStep} />}
          {step === 3 && <MinorWorksStep3 formData={formData} onPrev={prevStep} user={user} editCertId={editCertId} />}
        </div>
      </div>
    </div>
  );
}
